import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { LegendeEtapes, Timeline } from "@/composants/timeline";
import { Documents } from "@/composants/documents";
import { FichesDuLot } from "@/composants/fiches";
import { CloturerSurPreuve } from "@/composants/preuve-realisation";
import { FormulaireLot, type ValeursLot } from "@/composants/formulaire-lot";
import { AdresseEnBloc } from "@/composants/champs-commande";
import { LignesFournisseurDuLot } from "@/composants/commandes-fournisseurs";
import { messageDe, useChargement } from "@/lib/chargement";
import { euros, statutLot } from "@/lib/libelles";
import { api, type FicheLot } from "@/lib/api";
import { useSession } from "@/session";

/** La fiche d'un lot : son état, son adresse, sa timeline. */
export function PageLot() {
  const { id = "" } = useParams();
  const session = useSession();
  const identite = session.statut === "connecte" ? session.identite : null;
  const aLaPermission = identite?.permissions.includes("GERER_COMMANDE_CLIENT") ?? false;
  const voitLesMontants = identite?.permissions.includes("CONSULTER_MONTANTS") ?? false;
  const peutDeposer = identite?.permissions.includes("DEPOSER_DOCUMENT") ?? false;
  // Le terrain ne lit pas les fournisseurs (`profils.lit_les_fournisseurs`).
  const suitLesFournisseurs = identite?.lit_les_fournisseurs ?? false;

  const { donnees, erreur, enCours, recharger } = useChargement<FicheLot>(
    (signal) => api.lot(id, signal),
    [id],
  );
  const [corrige, setCorrige] = useState<FicheLot | null>(null);
  const [correction, setCorrection] = useState(false);
  const [action, setAction] = useState<string | null>(null);

  const lot = corrige ?? donnees;
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!lot || enCours) return <Chargement message="Chargement du lot…" />;
  // Habilité en consultation : la fiche sans ses gestes (ADR-0032).
  const peutGerer = aLaPermission && lot.intervenable;

  const valeurs: ValeursLot = {
    intitule: lot.intitule,
    categorie: lot.categorie,
    montant_ht_quote_part:
      lot.montant_ht_quote_part === null ? null : String(lot.montant_ht_quote_part),
    commentaires: lot.commentaires,
  };

  return (
    <div className="flex flex-col gap-5">
      <Link
        to={`/commandes/${lot.commande_client_id}`}
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Commande / Projet {lot.numero_commande_client}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-mono text-xl font-semibold text-encre">{lot.code_lot}</h1>
          <p className="mt-1 text-sm text-encre-douce">
            {lot.intitule} — {lot.categorie_libelle}
          </p>
        </div>
        <Etiquette ton={lot.statut === "ANNULE" ? "alerte" : "neutre"}>
          {statutLot(lot.statut_affiche)}
        </Etiquette>
      </div>

      {action ? <Erreur message={action} /> : null}

      <Carte>
        <EnTeteCarte className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-encre">Le lot</h2>
          {peutGerer ? (
            <Bouton taille="compact" variante="discret" onClick={() => setCorrection((o) => !o)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              {correction ? "Fermer" : "Corriger"}
            </Bouton>
          ) : null}
        </EnTeteCarte>
        <CorpsCarte>
          {correction ? (
            <FormulaireLot
              initial={valeurs}
              libelleValider="Enregistrer"
              onAnnuler={() => setCorrection(false)}
              onValider={async (nouvelles) => {
                setAction(null);
                try {
                  setCorrige(await api.corrigerLot(lot.id, nouvelles));
                  setCorrection(false);
                } catch (cause) {
                  setAction(messageDe(cause));
                }
              }}
            />
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
              {/* L'adresse est celle de la commande (ADR-0031) : elle se corrige là. */}
              <Donnee libelle="Adresse de réalisation">
                <AdresseEnBloc adresse={lot.adresse_realisation} />
              </Donnee>
              {voitLesMontants ? (
                <Donnee libelle="Quote-part HT">
                  <span className="tabular-nums">{euros(lot.montant_ht_quote_part)}</span>
                </Donnee>
              ) : null}
              <Donnee libelle="Statut opérationnel">{statutLot(lot.statut)}</Donnee>
              {lot.commentaires ? (
                <div className="col-span-full">
                  <Donnee libelle="Commentaires">{lot.commentaires}</Donnee>
                </div>
              ) : null}
            </dl>
          )}
          {/* Vente sans pose : déposer le document signé et clôturer font un
              seul geste (ADR-0033). */}
          {peutGerer && peutDeposer && lot.statut === "EN_COURS" && !correction ? (
            <CloturerSurPreuve
              lot={lot}
              onClos={async () => {
                setAction(null);
                try {
                  setCorrige(await api.lot(lot.id));
                } catch (cause) {
                  setAction(messageDe(cause));
                }
              }}
            />
          ) : null}
        </CorpsCarte>
      </Carte>

      {suitLesFournisseurs ? (
        <LignesFournisseurDuLot lotId={lot.id} voitLesMontants={voitLesMontants} />
      ) : null}

      <FichesDuLot lotId={lot.id} ecriture={lot.intervenable && peutDeposer} />

      <Documents
        // La preuve de réalisation se dépose aussi plus haut, et clôt le lot :
        // le statut change, la liste se recharge.
        key={lot.statut}
        cible={{ lot_id: lot.id }}
        ecriture={lot.intervenable}
        destinations={[
          {
            valeur: lot.id,
            libelle: lot.code_lot,
            cible: { lot_id: lot.id },
          },
        ]}
        categorieProposee="PHOTO_CHANTIER"
      />

      <LegendeEtapes />
      <Carte>
        <EnTeteCarte>
          <h2 className="text-sm font-semibold text-encre">Où en est ce lot</h2>
        </EnTeteCarte>
        <CorpsCarte>
          <Timeline etapes={lot.etapes} prochaineAction={lot.prochaine_action} />
        </CorpsCarte>
      </Carte>
    </div>
  );
}
